import { useState, useEffect } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { UniversalPickerMode, PickerConfig } from "../types";

export const usePickerData = (
  isOpen: boolean,
  mode: UniversalPickerMode,
  searchTerm: string,
  config: PickerConfig,
  onAutoSelect?: (value: any) => void,
) => {
  const { session } = useSession();
  const [contacts, setContacts] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [frequentSuppliers, setFrequentSuppliers] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [apiData, setApiData] = useState<any[]>([]);
  const [epics, setEpics] = useState<any[]>([]);
  const [renovationProjects, setRenovationProjects] = useState<any[]>([]);
  const [projectParts, setProjectParts] = useState<any[]>([]);
  const [resources, setResources] = useState<any[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isLoadingApiData, setIsLoadingApiData] = useState(false);
  const [isLoadingEpics, setIsLoadingEpics] = useState(false);
  const [isLoadingRenovationProjects, setIsLoadingRenovationProjects] =
    useState(false);
  const [isLoadingProjectParts, setIsLoadingProjectParts] = useState(false);
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  // Reset data when modal closes or mode changes
  useEffect(() => {
    if (!isOpen) {
      setContacts([]);
      setSuppliers([]);
      setFrequentSuppliers([]);
      setAssets([]);
      setApiData([]);
      setEpics([]);
      setRenovationProjects([]);
      setProjectParts([]);
      setResources([]);
      setIsLoadingContacts(false);
      setIsLoadingSuppliers(false);
      setIsLoadingAssets(false);
      setIsLoadingApiData(false);
      setIsLoadingEpics(false);
      setIsLoadingRenovationProjects(false);
      setIsLoadingProjectParts(false);
      setIsLoadingResources(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setContacts([]);
    setSuppliers([]);
    setFrequentSuppliers([]);
    setAssets([]);
    setApiData([]);
    setEpics([]);
    setRenovationProjects([]);
    setProjectParts([]);
    setResources([]);
    setIsLoadingContacts(false);
    setIsLoadingSuppliers(false);
    setIsLoadingAssets(false);
    setIsLoadingApiData(false);
    setIsLoadingEpics(false);
    setIsLoadingRenovationProjects(false);
    setIsLoadingProjectParts(false);
    setIsLoadingResources(false);
  }, [mode]);

  // Fetch data based on mode
  useEffect(() => {
    if (!isOpen || !session) return;

    if (mode === "epic" && epics.length === 0) {
      fetchEpics();
    } else if (mode === "renovation-project") {
      const delayedFetch = setTimeout(() => {
        // If building is selected, fetch immediately (no minimum search length)
        // Otherwise, require at least 2 characters
        if (config.currentBuildingId || searchTerm.length >= 2) {
          fetchRenovationProjects();
        } else {
          setRenovationProjects([]);
        }
      }, 300);
      return () => clearTimeout(delayedFetch);
    } else if (mode === "project-part") {
      // If renovation project is selected, fetch immediately (no minimum search length)
      // Otherwise, require project selection first
      if (config.currentRenovationProjectId) {
        fetchProjectParts();
      }
    } else if (mode === "supplier" && suppliers.length === 0) {
      fetchSuppliers();
      if (
        config.showFrequentSuppliers &&
        config.ticketIds &&
        config.ticketIds.length > 0
      ) {
        fetchFrequentSuppliersForTickets();
      }
    } else if (mode === "asset") {
      const delayedFetch = setTimeout(() => {
        if (searchTerm.length >= 2) {
          fetchAssets();
        } else {
          setAssets([]);
        }
      }, 300);
      return () => clearTimeout(delayedFetch);
    } else if (mode === "reporter") {
      const delayedFetch = setTimeout(() => {
        if (searchTerm.length >= 2) {
          fetchContacts();
        } else {
          setContacts([]);
        }
      }, 300);
      return () => clearTimeout(delayedFetch);
    } else if (mode === "resource") {
      // Resource search with debouncing
      const delayedFetch = setTimeout(() => {
        if (searchTerm.length >= 2) {
          fetchResources();
        } else {
          setResources([]);
        }
      }, 300);
      return () => clearTimeout(delayedFetch);
    } else if (config.apiConfig) {
      // Generic API data fetching for any mode with apiConfig
      const apiConfig = config.apiConfig;

      // Skip fetch if endpoint uses {contextValue} placeholder but no contextValue is provided
      if (apiConfig.endpoint?.includes('{contextValue}') && !config.contextValue) {
        setApiData([]);
        return;
      }

      const minLength = apiConfig.minSearchLength || 0;
      const debounceMs = apiConfig.debounceMs || 300;

      if (apiConfig.searchParam && minLength > 0) {
        // Debounced search
        const delayedFetch = setTimeout(() => {
          if (searchTerm.length >= minLength) {
            fetchApiData();
          } else {
            setApiData([]);
          }
        }, debounceMs);
        return () => clearTimeout(delayedFetch);
      } else {
        // Immediate fetch (no search term required)
        fetchApiData();
      }
    }
  }, [
    isOpen,
    mode,
    searchTerm,
    config.ticketIds,
    config.apiConfig,
    config.apiConfig?.placeholderValues?.unitId,
    config.contextValue,
    config.currentBuildingId,
    config.currentRenovationProjectId,
    session,
    suppliers.length,
    epics.length,
  ]);

  const fetchEpics = async () => {
    setIsLoadingEpics(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/epics`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setEpics(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch epics:", error);
      setEpics([]);
    } finally {
      setIsLoadingEpics(false);
    }
  };

  const fetchRenovationProjects = async () => {
    setIsLoadingRenovationProjects(true);
    try {
      // Build URL with optional buildingId filter
      let url = `${API_URL}/renovation/projects/search?q=${encodeURIComponent(searchTerm)}`;
      if (config.currentBuildingId) {
        url += `&buildingId=${encodeURIComponent(config.currentBuildingId)}`;
      }

      const response = await fetchWithAuth(url, { method: "GET" }, session);
      if (response.ok) {
        const data = await response.json();
        setRenovationProjects(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch renovation projects:", error);
      setRenovationProjects([]);
    } finally {
      setIsLoadingRenovationProjects(false);
    }
  };

  const fetchProjectParts = async () => {
    setIsLoadingProjectParts(true);
    try {
      if (!config.currentRenovationProjectId) {
        setProjectParts([]);
        return;
      }
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${config.currentRenovationProjectId}/parts`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setProjectParts(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch project parts:", error);
      setProjectParts([]);
    } finally {
      setIsLoadingProjectParts(false);
    }
  };

  const fetchSuppliers = async () => {
    setIsLoadingSuppliers(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/companies`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
      setSuppliers([]);
    } finally {
      setIsLoadingSuppliers(false);
    }
  };

  const fetchFrequentSuppliersForTickets = async () => {
    try {
      if (!config.ticketIds || config.ticketIds.length === 0) return;

      const ticketLabels: string[] = [];

      for (const ticketId of config.ticketIds) {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/helpdesk/ticket/${ticketId}`,
            { method: "GET" },
            session,
          );
          if (response.ok) {
            const ticket = await response.json();
            if (ticket.label && ticket.label !== "UNKNOWN") {
              ticketLabels.push(ticket.label);
            }
          }
        } catch (error) {
          console.error(`Failed to fetch ticket ${ticketId}:`, error);
        }
      }

      if (ticketLabels.length === 0) return;

      const uniqueLabels = [...new Set(ticketLabels)];
      const allFrequentSuppliers: any[] = [];

      for (const label of uniqueLabels) {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/helpdesk/suppliers/frequent-by-label/${encodeURIComponent(label)}`,
            { method: "GET" },
            session,
          );
          if (response.ok) {
            const data = await response.json();
            allFrequentSuppliers.push(...data);
          }
        } catch (error) {
          console.error(
            `Failed to fetch frequent suppliers for ${label}:`,
            error,
          );
        }
      }

      const supplierMap = new Map();
      allFrequentSuppliers.forEach((item) => {
        const supplierId = item.supplier.id;
        if (supplierMap.has(supplierId)) {
          const existing = supplierMap.get(supplierId);
          existing.ticketCount += item.ticketCount;
          if (new Date(item.lastUsed) > new Date(existing.lastUsed)) {
            existing.lastUsed = item.lastUsed;
          }
        } else {
          supplierMap.set(supplierId, { ...item });
        }
      });

      const uniqueSuppliers = Array.from(supplierMap.values());
      uniqueSuppliers.sort((a: any, b: any) => b.ticketCount - a.ticketCount);

      const topSuppliers = uniqueSuppliers
        .filter((supplier: any) => supplier.ticketCount >= 1)
        .slice(0, 5);

      setFrequentSuppliers(topSuppliers);
    } catch (error) {
      console.error("Failed to fetch frequent suppliers:", error);
      setFrequentSuppliers([]);
    }
  };

  const fetchAssets = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setAssets([]);
      return;
    }

    setIsLoadingAssets(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets?search=${encodeURIComponent(searchTerm)}`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setAssets(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch assets:", error);
      setAssets([]);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const fetchContacts = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setContacts([]);
      return;
    }

    setIsLoadingContacts(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/search?query=${encodeURIComponent(searchTerm)}`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setContacts(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      setContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const fetchApiData = async () => {
    if (!config.apiConfig) return;

    setIsLoadingApiData(true);
    try {
      const apiConfig = config.apiConfig;

      // Build endpoint URL with placeholder replacement
      let endpoint = apiConfig.endpoint;
      if (apiConfig.placeholderValues) {
        Object.entries(apiConfig.placeholderValues).forEach(([key, value]) => {
          endpoint = endpoint?.replace(`{${key}}`, value);
        });
      }
      // Also replace {contextValue} with the dynamic context value from the entity
      if (config.contextValue && endpoint) {
        endpoint = endpoint.replace('{contextValue}', String(config.contextValue));
      }

      // Add search parameter if provided
      const url = new URL(endpoint as string, API_URL);
      if (apiConfig.searchParam && searchTerm) {
        url.searchParams.set(apiConfig.searchParam, searchTerm);
      }

      // Prepare request options
      const requestOptions: any = {
        method: apiConfig.method || "GET",
        headers: {
          "Content-Type": "application/json",
          ...apiConfig.headers,
        },
      };

      if (apiConfig.body && apiConfig.method === "POST") {
        requestOptions.body = JSON.stringify(apiConfig.body);
      }

      const response = await fetchWithAuth(
        url.toString(),
        requestOptions,
        session,
      );

      if (response.ok) {
        let data = await response.json();

        // Extract data from nested path if specified
        if (apiConfig.dataPath) {
          const pathParts = apiConfig.dataPath.split(".");
          for (const part of pathParts) {
            data = data?.[part];
          }
        }

        const finalData = Array.isArray(data) ? data : [data].filter(Boolean);
        setApiData(finalData);

        // Auto-select if only one contract found for rental-contract mode (only if not manually triggered)
        if (
          mode === "rental-contract" &&
          finalData.length === 1 &&
          onAutoSelect &&
          !config.disableAutoSelect
        ) {
          setTimeout(() => onAutoSelect(finalData[0]), 0); // Use timeout to avoid state update during render
        }
      } else {
        console.error(
          `API request failed: ${response.status} ${response.statusText}`,
        );
        setApiData([]);
      }
    } catch (error) {
      console.error("Failed to fetch API data:", error);
      setApiData([]);
    } finally {
      setIsLoadingApiData(false);
    }
  };

  const fetchResources = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setResources([]);
      return;
    }

    setIsLoadingResources(true);
    try {
      // Build filter parameter - default to contact,company for file log relations
      const typeFilter = config.resourceTypeFilter?.join(',') || 'contact,company';

      const response = await fetchWithAuth(
        `${API_URL}/resource/search-direct?query=${encodeURIComponent(searchTerm)}&filter=${typeFilter}`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setResources(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch resources:", error);
      setResources([]);
    } finally {
      setIsLoadingResources(false);
    }
  };

  return {
    contacts,
    suppliers,
    frequentSuppliers,
    assets,
    apiData,
    epics,
    renovationProjects,
    projectParts,
    resources,
    isLoadingContacts,
    isLoadingSuppliers,
    isLoadingAssets,
    isLoadingApiData,
    isLoadingEpics,
    isLoadingRenovationProjects,
    isLoadingProjectParts,
    isLoadingResources,
  };
};
